import React, { useEffect, useState } from "react";
import { classRoomStudentApi } from "../../../../api/class-room-student/class-room-student.api";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import StatusCustom from "../../../../components/status/statusCustom";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

// ---------- Types ----------
export type Person = {
    id: string | number;
    name: string;
    email?: string;
    avatarUrl?: string;
    tags?: string[]; // ex. ["อาจารย์ที่ปรึกษาร่วม"]
    homeroom?: string; // ประจำชั้น
    level?: string; // ระดับ
    faculty?: string; // คณะ
    year?: string | number; // ชั้นปี
    room?: string | number; // ห้อง
    status?: string;
};

// ---------- Small bits ----------
const Badge: React.FC<{ label: string }> = ({ label }) => (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm leading-none text-blue-700 ring-1 ring-inset ring-blue-100">
        <span className="h-2 w-2 rounded-full bg-blue-500" />
        {label}
    </span>
);

const InfoRow: React.FC<{ label: string; value?: React.ReactNode }>
    = ({ label, value }) => (
        <div className="flex gap-x-3 text-[15px] leading-6 text-slate-700">
            <div className="text-slate-500">{label}</div>
            <div className="font-medium">{value ?? "-"}</div>
        </div>
    );

// ---------- Card ----------
const ProfileCard: React.FC<{ person: Person }> = ({ person }) => {
    const { t } = useTranslation();
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex flex-col gap-6 md:flex-row md:items-start">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    <div className="size-28 overflow-hidden rounded-2xl ring-1 ring-slate-200">
                        {person.avatarUrl ? (
                            <img src={person.avatarUrl} alt={person.name} className="h-full w-full object-cover" />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center bg-pink-100 text-3xl font-bold text-pink-700">
                                {person.name?.[0] ?? "?"}
                            </div>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1">
                    <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
                        <div className="flex gap-10 justify-start">
                            <h2 className="text-xl font-semibold text-slate-900">
                                {person.name}
                            </h2>
                            <div>
                                <StatusCustom
                                    disableMaxWidth
                                    sx={{ padding: "0 2em" }}
                                    text={person.status || ""}
                                    type={person.status || ""}
                                />
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {person.tags?.map((t, i) => (<Badge key={i} label={t} />))}
                        </div>
                    </div>

                    {person.email && (
                        <div className="mt-1 text-sm text-slate-500">{person.email}</div>
                    )}
                    <div className="mt-5 flex flex-col gap-2">
                        <Typography variant="subtitle1" fontWeight={700}>
                            {t("ประจำชั้น")}
                        </Typography>
                        <InfoRow label={`${t("ระดับ :")} `} value={person.level} />
                        <InfoRow label={`${t("หลักสูตร :")} `} value={person.faculty} />
                        <InfoRow label={`${t("ชั้นปี :")} `} value={person.year} />
                        <InfoRow label={`${t("ห้อง :")} `} value={person.room} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------- List Container ----------
export default function StudentProfileCards() {
    const base_url = useSelector((state: RootState) => state.api.image_api);
    const [data, set_data] = useState<any[]>([]);
    const getDataAdvisor = async () => {
        await classRoomStudentApi.getClassRoomStudent().then((res) => {
            if (res.status < 300) {
                const data = res.data;
                set_data(data.data);
            } else {
                set_data([]);
            }
        }).catch((err) => {
            console.log(err)
        })
    };

    useEffect(() => {
        getDataAdvisor();
    }, [])
    return (
        <div className="w-full grid grid-cols-12 gap-4">
            {data?.map((p) => {
                const fullname = (p?.first_name && p?.last_name) ? `${p?.first_name} ${p?.last_name}` : '-'
                return (
                    <span key={p.id} className="col-span-12 xl:col-span-6">
                        <ProfileCard
                            person={{
                                id: p?.id_class_room_employee,
                                name: fullname,
                                avatarUrl: `${base_url}/${p?.file_path}`,
                                room: p?.room,
                                status: p?.advisor_type,
                                faculty: p?.faculty_name,
                                level: p?.degree_name,
                                homeroom: "",
                                email: p?.email_organization,
                                year:p?.term
                            }}
                        />
                    </span>
                )
            })}
        </div>
    );
}

/*
Usage:

import StudentProfileCards, { Person } from "./StudentProfileCards";

const data: Person[] = [
  {
    id: 1,
    name: "นางสาวชุติมา จันทร์ดี",
    email: "Shutima@gmail.com",
    avatarUrl: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&auto=format&fit=crop",
    tags: ["อาจารย์ที่ปรึกษาร่วม"],
    homeroom: "",
    level: "ปริญญาตรี",
    faculty: "พุทธศึกษา",
    year: 1,
    room: 2,
  },
];

< StudentProfileCards data={data} />
*/
